import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import { PagerTargetEvent } from '@progress/kendo-react-data-tools';
import {
  GridCellProps,
  GridCustomCellProps,
  GridPageChangeEvent,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid';
import { produce } from 'immer';
import _ from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid, { GridCellTd } from '../../../../../../../common/widgets/Grid';
import Loader from '../../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../../common/widgets/LoadingSpinner';
import { config } from '../../../../../config';
import { StateHistoryModel } from '../../../../../models/StateHistoryModel';
import { resources } from '../../../../../resources/resources';
import { employeeAdminService } from '../../../../../services/employee-admin';

interface StateHistoryProps {
  employeeId: number;
}

interface StatusHistoryState {
  stateHistory?: StateHistoryModel[];
  showErrorPanel: boolean;
}

interface PageState {
  skip: number;
  take: number;
}

const DateAndHourCell = (props: GridCustomCellProps) => {
  const { dataItem } = props;
  return (
    <GridCellTd>
      {moment(dataItem.dateAdded).format('YYYY.MM.DD HH:mm')}
    </GridCellTd>
  );
};

const commentCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd className="grid-comment">{dataItem.comment}</GridCellTd>
);

const changedByCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>{dataItem.changedBy}</GridCellTd>
);

const StatusCell = (props: GridCustomCellProps) => {
  const { dataItem } = props;
  const { t: translate } = useTranslation();
  const activeColor = '#c9de97';
  const deactivatedColor = '#d9c1d8';

  if (_.isEmpty(dataItem.state)) {
    return <GridCellTd />;
  }

  if (dataItem.state !== 'enabled' && dataItem.state !== 'disabled') {
    return <GridCellTd>{dataItem.state}</GridCellTd>;
  }

  return (
    <GridCellTd>
      <span
        className="grid-status-badge"
        style={{
          backgroundColor: `${
            dataItem.state === 'enabled' ? activeColor : deactivatedColor
          }`,
        }}
      >
        {dataItem.state && dataItem.state === 'enabled'
          ? translate(resources.admin.employees.details.stateHistory.approved)
          : translate(
              resources.admin.employees.details.stateHistory.notApproved,
            )}
      </span>
    </GridCellTd>
  );
};

export default function StatusHistory(props: Readonly<StateHistoryProps>) {
  const { t: translate } = useTranslation();
  const { employeeId } = props;
  const [state, setState] = useState<StatusHistoryState>(
    {} as StatusHistoryState,
  );
  const initialSort = useMemo(
    () =>
      [
        {
          field: 'dateAdded',
          dir: 'desc',
        },
      ] as SortDescriptor[],
    [],
  );
  const initialPageState: PageState = {
    skip: 0,
    take: config.grid.defaultPageSize,
  };
  const [page, setPage] = useState<PageState>(initialPageState);
  const [sort, setSort] = useState<SortDescriptor[]>(initialSort);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >(config.grid.defaultPageSize);

  const loadStateHistory = useCallback(
    async (abortController?: AbortController) => {
      const response = await employeeAdminService.getStatusHistory(employeeId, {
        signal: abortController?.signal,
      });
      if (!response.hasErrors) {
        setState(
          produce(d => {
            d.stateHistory = orderBy(response.data, initialSort);
          }),
        );
      } else {
        setState(
          produce(d => {
            d.showErrorPanel = true;
          }),
        );
      }
    },
    [employeeId, initialSort],
  );

  const handleSortChange = (e: GridSortChangeEvent) => {
    setState(
      produce(draft => {
        draft.stateHistory = orderBy(state.stateHistory ?? [], e.sort);
      }),
    );
    setSort(e.sort);
  };

  const handlePageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === 'All'
        ? state.stateHistory?.length ?? 0
        : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  useEffect(() => {
    loadStateHistory();
  }, [loadStateHistory]);

  return (
    <div>
      <Loader
        loading={!state.stateHistory}
        loader={<LoadingSpinner />}
        showErrorPanel={state.showErrorPanel}
        errorPanelMessage={''}
      >
        <div className="admin-grid employee-history-grid">
          <Grid
            data={orderBy(
              state.stateHistory?.length
                ? state.stateHistory.slice(page.skip, page.take + page.skip)
                : [],
              sort,
            )}
            pageable={{
              pageSizes: config.grid.pageSizes,
              pageSizeValue,
              info: false,
              previousNext: true,
            }}
            skip={page.skip}
            take={page.take}
            total={state.stateHistory?.length ?? 0}
            sortable={{ allowUnsort: true, mode: 'multiple' }}
            sort={sort}
            onSortChange={handleSortChange}
            onPageChange={handlePageChange}
            columns={[
              {
                title: translate(
                  resources.admin.employees.details.stateHistory.date,
                ),
                field: 'dateAdded',
                cell: DateAndHourCell,
              },
              {
                title: translate(
                  resources.admin.employees.details.stateHistory.comment,
                ),
                field: 'comment',
                cell: commentCell,
                headerClassName: 'grid-comment',
              },
              {
                title: translate(
                  resources.admin.employees.details.stateHistory.state,
                ),
                field: 'state',
                cell: StatusCell,
              },
              {
                title: translate(
                  resources.admin.employees.details.stateHistory.aprovedBy,
                ),
                field: 'changedBy',
                cell: changedByCell,
              },
            ]}
          />
        </div>
      </Loader>
    </div>
  );
}
